import { Fragment, useState, useRef, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import parse from 'html-react-parser';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import ChordPiano from '../../../../components/ChordPiano';
import ChordBarsChart from '../../../../components/ChordBarsChart';
import PartAssignments from '../../../../components/PartAssignments';
import PartOptionsModal from '../../../../components/modals/PartOptionsModal';
import PartMidiModal from '../../../../components/modals/PartMidiModal';
import ChordBarsPlayerModal from '../../../../components/modals/ChordBarsPlayerModal';
import { canArrange } from '../../../../components/IntensityPanel';
import { useChartRunEntry } from '../../../../context/ChartRunContext';

import theme from '../../../../utils/theme';
import auth from '../../../../services/authService';
import { replaceChords } from '../../../../utils/chordPatterns';
import { INVERSION_ORDINALS, splitInversion } from '../../../../utils/chordNotes';
import { getSectionStartBars, lyricsMatchChart } from '../../../../utils/chordBars';
import { getPartGrid, isChartDrawn } from '../../../../utils/partCharts';
import { getBeatsPerBar, getRepeatCount } from '../../../../utils/songTiming';
import { getChartInstrumentId, getPartInstrumentId } from '../../../../utils/midiInstruments';
import { getChartRhythm } from '../../../../utils/chordRhythmPatterns';
import { toIntensityEntry } from '../../../../utils/chartIntensity';
import RepeatCounter from '../RepeatCounter';
import SongTransition from '../SongTransition';
import PartTempoMarker from '../PartTempoMarker';
import { applyRepeatSlashes, parseRepeatDirective } from '../../../../utils/repeat';
import { getSetting, settingKeys } from '../../../../utils/settings';
import { isSectionPart } from '../../../../utils/partNumbers';
import { isNotePart } from '../../../../utils/notes';
import { getInstrumentLabels, getMelodySection, getPeopleLabels, isDrumSolo } from '../../../../utils/parts';
import { getMidiTransposition, hasMidi } from '../../../../utils/midiJson';
import { getPartOriginalKey, spellChord } from '../../../../utils/partKey';
import { getChordFamily, getFamilyLabel } from '../../../../utils/chordLibrary';

import { ReactComponent as EyeClosedIcon } from '../../../../icons/eye-closed.svg';
import { ReactComponent as RepeatIcon } from '../../../../icons/repeat.svg';
import { ReactComponent as PlayIcon } from '../../../../icons/play-solid.svg';
import { ReactComponent as ChordBarsIcon } from '../../../../icons/chord-bars.svg';

import './ViewSongPart.scss';

const TICK_MS = 50;

const partNameToClass = (name) => name.toLowerCase().replace(/\s+/g, '-');

const ViewSongPart = ({
  part,
  displayNumber = part.number,
  songTeamId,
  songId,
  songKey,
  songOriginalKey,
  partKey,
  previousPartKey,
  songTimeSignature,
  songCategory,
  songChartInstrumentId,
  songChartRhythm,
  songChartEffects,
  songBpm,
  partBpm,
  tempoEnter,
  tempoExit,
  onTempoTap,
  transposeSemitones,
  attachedNotes = [],
  writtenPart,
  disablePartOptions = false,
}) => {
  const { t } = useTranslation();
  const user = auth.getCurrentUser();
  // Hidden notes are only visible to admins of the team that owns the song
  const isAdmin = user && user.isAdmin && user.teamId === songTeamId;
  const isOwnTeam = user && user.teamId === songTeamId;
  const showDuplicateIcon = getSetting(settingKeys.showDuplicateIcon) === true;
  const showMidiIcon = getSetting(settingKeys.showMidiIcon) !== false; // Default to true
  const showChordOnTap = getSetting(settingKeys.showChordOnTap) === true;
  const repeatCountAsChip = getSetting(settingKeys.repeatCountAsChip) === true;
  const colorBorrowedChords = getSetting(settingKeys.colorBorrowedChords) === true;

  const history = useHistory();
  const location = useLocation();
  const [selectedChord, setSelectedChord] = useState(null);
  const [anchorRect, setAnchorRect] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMidiModalOpen, setIsMidiModalOpen] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);
  // The chart opened in the player, grid and sections both
  const [selectedChart, setSelectedChart] = useState(null);
  // Where this part hands its chart to a run played through the song — set further down, once the
  // pieces of it are defined
  const runEntryRef = useChartRunEntry(part.id);

  // Both come from getPartKeys, which holds the carry-forward rule: `partKey` is the key this part
  // is in whether it declared that itself or inherited it, and `previousPartKey` is set only on the
  // part that actually moves the key. So the change is announced once, where it happens, and the
  // parts below that merely inherit it say nothing.
  const currentPartKey = partKey || songKey;
  const showKeyChange = Boolean(previousPartKey);

  const { count, duration } = parseRepeatDirective(part.lyrics);
  const hasCounter = count !== null && count >= 1;
  const hasRepeat = part.repeatCount >= 2;

  const beatsPerBar = getBeatsPerBar(songTimeSignature);
  // What the song's charts play with where a part doesn't say
  const songChart = { chartInstrumentId: songChartInstrumentId, chartRhythm: songChartRhythm };
  // Read off the part as written: Hide Chords takes an instrumental's chart off the page (see
  // removeChords), but it can still be opened and played
  const partChart = getPartGrid(writtenPart ?? part, beatsPerBar);
  // An instrumental's chart is its body, drawn under whatever words it has — unless it's hidden
  const drawnChart = isChartDrawn(part) ? getPartGrid(part, beatsPerBar) : null;

  // repeatCountAsChip surfaces the repeat as an "xN" badge instead of slashes in the lyrics. A drawn
  // chart draws repeat signs instead, so the slashes stay off its part too. Slashes typed by hand
  // are untouched either way.
  const withRepeatSigns = (text) => {
    const { strippedLyrics: words } = parseRepeatDirective(text);

    return repeatCountAsChip || drawnChart ? words : applyRepeatSlashes(words, part.repeatCount, part.name);
  };

  const repeatedLyrics = withRepeatSigns(part.lyrics);

  // The chip already says "xN" where it is on, so the signs would be saying it twice
  const hasChartRepeat = Boolean(drawnChart) && hasRepeat && !repeatCountAsChip;

  // The words the player follows the chart through. Normally the ones on the page — but the reader
  // may be hiding chords (the plugin rewrites the part, see removeSongChords), and a chart followed
  // through words with no chords over them has nothing to light and would take the Lyrics view away
  // exactly when it is most use: a singer practising off the page they sing from.
  const chartWords = writtenPart ? withRepeatSigns(writtenPart.lyrics) : repeatedLyrics;

  // A chart can be played back at the song's own tempo — which is the whole of what the player has
  // to go on, so a song with no BPM has nothing to play and the chart stays the notation it was.
  // The printed sheet passes disablePartOptions and is inert for the same reason the chip is there.
  // A part with a tempo of its own is played at it
  const effectiveBpm = partBpm || songBpm;
  const canPlayChart = !disablePartOptions && Number(effectiveBpm) > 0;

  // A chart not drawn on the page — a part made of words keeps its chart beside them, and Hide
  // Chords takes an instrumental's off — has nothing to tap, so the chip carries an icon and the
  // part options open the player.
  const attachedChart = drawnChart ? null : partChart;
  const canPlayAttachedChart = Boolean(attachedChart) && canPlayChart;

  // A part ending in text needs both breaks below to leave a blank line before the next one. A chart
  // is a block and has already ended its line, so the first break would land a second blank line
  // under it and push the part apart from its neighbours.
  const endsWithChart = Boolean(drawnChart);

  // A label already says what plays, so the '♫' standing in for hidden chords is dropped when
  // one is shown. Parts with no instruments keep the note.
  const showsInstrumentLabels = isOwnTeam && part.assignedInstruments?.length > 0;

  // Mutable counter state kept in a ref to avoid stale closures inside the interval
  const counterRef = useRef({ phase: 'idle', activeIdx: 0, progress: 0 });
  const [counterState, setCounterState] = useState({ phase: 'idle', activeIdx: 0, progress: 0 });
  const intervalRef = useRef(null);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const updateCounter = (newState) => {
    counterRef.current = newState;
    setCounterState(newState);
  };

  const startFrom = (idx) => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    updateCounter({ phase: 'running', activeIdx: idx, progress: 0 });

    const increment = TICK_MS / Math.max(duration * 1000, 100);

    intervalRef.current = setInterval(() => {
      const s = counterRef.current;

      if (s.phase !== 'running') return;

      const newProgress = s.progress + increment;

      if (newProgress >= 1) {
        if (s.activeIdx < count - 1) {
          updateCounter({ phase: 'running', activeIdx: s.activeIdx + 1, progress: 0 });
        } else {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          updateCounter({ phase: 'done', activeIdx: count - 1, progress: 1 });
        }
      } else {
        updateCounter({ ...s, progress: newProgress });
      }
    }, TICK_MS);
  };

  const handleTap = () => {
    if (!hasCounter) return;

    const { phase, activeIdx } = counterRef.current;

    if (phase === 'idle' || phase === 'done') {
      startFrom(0);
    } else if (activeIdx < count - 1) {
      startFrom(activeIdx + 1);
    } else {
      // Tap on last active circle → restart
      startFrom(0);
    }
  };

  // Hidden Notes are only visible to admins of the owning team
  if (part.isHidden && !isAdmin) return null;
  // Note parts are only visible to members of the owning team
  if (part.name === 'Note' && !isOwnTeam) return null;

  // Section parts mark the start of a new song in a mix — render the typed text as a large heading
  if (isSectionPart(part)) {
    // Shown in a fully charted song's run as the heading it is here, and never played
    runEntryRef.current = () => ({ partId: part.id, name: part.name, title: part.lyrics, section: true });

    return (
      <h2 key={part.id} className={theme.renderPartColor(part.name, 'ViewSongPart section-title')}>
        {part.lyrics}
      </h2>
    );
  }

  // Admins always have something in the options modal (Copy, Edit). Everyone else has whatever the
  // part carries — a MIDI clip, a chart of its own, or neither, in which case the chip does nothing.
  // disablePartOptions covers the printed sheet, where none of them mean anything.
  const canOpenPartOptions = !disablePartOptions && (isAdmin || hasMidi(part) || canPlayAttachedChart);
  const musicTagClass = `music-tag${canOpenPartOptions ? ' clickable' : ''}`;

  // A chord's Chord Library colour, only when it is outside the part's key — see colorBorrowedChords
  const outOfKeyFamily = (chord) => {
    const family = getChordFamily(currentPartKey, chord);

    return family === 'key' ? null : family;
  };

  // A chart's chords, spelled to the part's key as its lyrics are in matchChords
  const spellPartChord = (chord) => spellChord(chord, currentPartKey);

  // The tapped chord's scale badge follows the same setting as its colour
  const selectedFamily = colorBorrowedChords && selectedChord ? outOfKeyFamily(selectedChord) : null;

  const matchChords = (part) => {
    let str = part;
    const supRX = /\d+/g;
    const superscriptQualifiers = getSetting(settingKeys.superscriptQualifiers) === true;
    const chordSymbols = getSetting(settingKeys.chordSymbols) === true;
    const qualifierRX = /sus|aug|dim|add/;

    str = replaceChords(str, (written) => {
      // Spelled to the part's key first, so the page, the piano and the colour all read one name
      const chord = spellChord(written, currentPartKey);

      // The inversion mark comes off before the figures are raised and goes back on after, as an
      // ordinal: raising its digit with the rest would make 'C^1' and 'C7' the same shape on the
      // page. data-chord below keeps the symbol whole, so the piano still reads what was written.
      const { symbol, inversion } = splitInversion(chord);

      let matched = symbol.replace(supRX, (sup) => `<sup>${sup}</sup>`);

      if (chordSymbols) {
        matched = matched
          .replace(/dim/, '<span class="chord-symbol-dim"><span class="chord-symbol-dim__char">°</span></span>')
          .replace(/aug/, '<sup>+</sup>');
      }

      if (superscriptQualifiers) {
        matched = matched.replace(qualifierRX, (q) => `<sup>${q}</sup>`);
      }

      if (inversion > 0) matched += `<sup>${INVERSION_ORDINALS[inversion]}</sup>`;

      const family = colorBorrowedChords ? outOfKeyFamily(chord) : null;

      return `<span class='chord${family ? ` chord-family--${family}` : ''}' data-chord="${chord}">${matched}</span>`;
    }, { display: true });

    return str;
  };

  // ((note)) — a Note part's styling inline, for a remark that doesn't need a part of its own.
  // Spans newlines, unlike the markers above, since a note is often more than one line.
  const matchNotes = (part) =>
    part.replace(/\(\(([\s\S]*?)\)\)/g, (matched, p1) => `<span class='part-note'>${p1}</span>`);

  // Inline markdown. Bold runs first so italic doesn't claim half of a `**`.
  const matchBold = (part) => part.replace(/\*\*(.*?)\*\*/g, (matched, p1) => `<strong>${p1}</strong>`);
  const matchItalic = (part) => part.replace(/\*(.*?)\*/g, (matched, p1) => `<em>${p1}</em>`);
  const matchUnderline = (part) => part.replace(/__(.*?)__/g, (matched, p1) => `<u>${p1}</u>`);

  const formatPart = (part) => matchNotes(matchUnderline(matchItalic(matchBold(matchChords(part)))));

  /**
   * Whether the player can follow this chart through the part's words instead of its grid.
   *
   * The chart has to be one kept off the lyrics — an instrumental part's is written in them, out of
   * chord lines, so it would match itself and draw its own bar lines as words — and the words have
   * to carry exactly the chords it plays, in the same order, which is what makes the nth chord it
   * strikes the nth chord on the page. Anything else and the view isn't offered rather than lighting
   * the wrong syllable.
   */
  const canFollowInLyrics = (chart) => Boolean(chart.isAttached) && lyricsMatchChart(chart.lines, chartWords);

  // Lyrics that come out to nothing: a '♫' an instrument label has taken over saying, or none
  const rendersNothing = (lyrics) => {
    const text = lyrics.trim();

    return !text || (showsInstrumentLabels && text === '♫');
  };

  const renderChart = (chart) => (
    <ChordBarsChart
      lines={chart.lines}
      // Which lines belong to which instrument, where the chart was written for more than one
      sections={chart.sections}
      repeatCount={hasChartRepeat ? part.repeatCount : undefined}
      // So a bar holding several chords draws them at the widths they will be played at
      beatsPerBar={songTimeSignature ? beatsPerBar : undefined}
      countInEntry={chart.countInEntry}
      onOpen={canPlayChart ? (e) => handleChartClick(e, chart) : undefined}
      chordFamily={colorBorrowedChords ? outOfKeyFamily : undefined}
      spellChord={spellPartChord}
    />
  );

  const renderLyrics = (lyrics) => (rendersNothing(lyrics) ? null : parse(formatPart(lyrics)));

  // Attached notes read as the part's opening lines: note styling, but above the lyrics. Same
  // audience as a standalone note — team members, and admins for the hidden ones.
  const visibleAttachedNotes = attachedNotes.filter((note) => isOwnTeam && (!note.isHidden || isAdmin));

  // An empty lyrics span and its break read as a blank line under the chip. The break stays for a
  // counter or a note, which are what it would then be separating.
  const hasLyricsRow = !rendersNothing(repeatedLyrics) || Boolean(drawnChart);
  const hasRowBelowChip = hasLyricsRow || hasCounter || visibleAttachedNotes.length > 0;

  const renderAttachedNote = (note) => (
    <Fragment key={note.id}>
      {/* A note's chords are marked up like any others, so they open the piano too — but a note has
          no repeat counter, so it takes the chord half of the tap and nothing else */}
      <span
        className={`music-tag-lyrics music-tag__${partNameToClass(note.name)} attached-note`}
        onClick={showChordOnTap ? handleNoteClick : undefined}
      >
        {note.isHidden && <EyeClosedIcon className="hidden-note-icon" aria-hidden="true" />}
        {parse(formatPart(note.lyrics))}
      </span>
      <br />
    </Fragment>
  );

  // Who the part is for, as its pills show it — to the team only. The run shows and says the same.
  const assignedPeople = isOwnTeam ? getPeopleLabels(part) : [];
  const assignedInstruments = isOwnTeam ? getInstrumentLabels(part) : [];

  // Only the label is translated: the stored name drives the color and the logic
  const partLabel = `${t(`parts.${part.name}`, { defaultValue: part.name })} ${displayNumber}`;

  const renderPartChip = (part) => (
    <span className="wrapper">
      <span
        className={theme.renderPartColor(part.name, musicTagClass)}
        onClick={canOpenPartOptions ? () => handlePartChipClick(part) : undefined}
      >
        <span className="name">
          {partLabel}
          {repeatCountAsChip && hasRepeat && part.name !== 'Note' && (
            <span className="repeat-badge">×{part.repeatCount}</span>
          )}
          {part.isHidden && <EyeClosedIcon className="hidden-note-icon" aria-hidden="true" />}
          {part.isDuplicate && showDuplicateIcon && part.name !== 'Note' && (
            <RepeatIcon className="duplicate-part-icon" aria-hidden="true" />
          )}
          {hasMidi(part) && showMidiIcon && <PlayIcon className="midi-part-icon" aria-hidden="true" />}
          {attachedChart && <ChordBarsIcon className="chart-part-icon" aria-hidden="true" />}
        </span>
      </span>
      <PartAssignments name={part.name} people={assignedPeople} instruments={assignedInstruments} />
    </span>
  );

  // An admin always has Copy and Edit, so there is always a choice to make. Everyone else has only
  // what the part happens to carry — a clip, a chart of its own, or both — and a modal holding a
  // single button is a tap in the way, so the one thing on offer opens straight away.
  const handlePartChipClick = (part) => {
    if (!canOpenPartOptions) return;

    setSelectedPart(part);

    const options = [...(hasMidi(part) ? [handlePlayMidi] : []), ...(canPlayAttachedChart ? [handlePlayChart] : [])];

    if (!isAdmin && options.length === 1) options[0]();
    else setIsModalOpen(true);
  };

  const handleCopyPart = async (part) => {
    try {
      await navigator.clipboard.writeText(part.lyrics);

      toast.info(t('view.partCopied', { part: partLabel }), {
        autoClose: 3000,
        hideProgressBar: true,
        draggable: false,
        progress: false,
      });
    } catch (error) {}
  };

  const handlePlayMidi = () => {
    setIsMidiModalOpen(true);
  };

  const handlePlayChart = () => {
    setSelectedChart({ ...attachedChart, isAttached: !isChartDrawn(part) });
  };

  const openInSongForm = (search) => {
    const fromUrl = location.pathname + location.search + location.hash;
    history.push({ pathname: `/songs/${songId}`, search, state: { from: fromUrl } });
  };

  const handleEditPart = (part) => openInSongForm(`?editPartId=${part.id}`);

  // Opens the song form with the intensity panel picked on this part
  const handleEditIntensity = (part) => openInSongForm(`?intensityPartId=${part.id}`);

  // A tap on a chord opens the piano over it. Returns whether it landed on one, so the lyrics row
  // can fall through to the repeat counter while a note — which has no counter — ignores the rest.
  const openChordAt = (e) => {
    if (!showChordOnTap) return false;

    const chordEl = e.target.closest?.('.chord');

    if (!chordEl?.dataset.chord) return false;

    e.stopPropagation();
    setSelectedChord(chordEl.dataset.chord);
    setAnchorRect(chordEl.getBoundingClientRect());

    return true;
  };

  const handleLyricsClick = (e) => {
    // Let double-click bubble uninterrupted to ViewBody's onDoubleClick (auto-scroll toggle)
    if (e.detail >= 2) return;
    if (openChordAt(e)) return;
    if (hasCounter) handleTap();
  };

  const handleNoteClick = (e) => {
    if (e.detail >= 2) return;

    openChordAt(e);
  };

  // Which instrument each of a chart's sections belongs to: the nth assigned instrument where the
  // chart is divided into sections. A chart written for the part as a whole names none, and takes
  // the melody whatever it is tagged with.
  const getChartSectionInstruments = (chart) => {
    const sectionCount = (chart.sections?.[chart.sections.length - 1] ?? 0) + 1;

    return sectionCount > 1 ? Array.from({ length: sectionCount }, (_, i) => part.assignedInstruments?.[i]) : [];
  };

  // The part's MIDI, if it belongs under the chart that was tapped. A clip played for the trumpet
  // would clash under the guitar's chords, so it is held back there rather than played over them.
  const getChartMelody = (chart) => {
    if (!hasMidi(part)) return null;

    const { plays, section } = getMelodySection(part, getChartSectionInstruments(chart));

    if (!plays) return null;

    return {
      midiJson: part.midi,
      instrumentId: getPartInstrumentId(part.midi),
      semitones: getMidiTransposition(
        part.midi,
        getPartOriginalKey(currentPartKey, transposeSemitones),
        transposeSemitones,
      ),
      // In a chart shared between instruments the clip starts where its own instrument comes in,
      // not at the top of the chart
      atBar: section > 0 ? getSectionStartBars(chart.lines, chart.sections)[section] || 0 : 0,
    };
  };

  // A chart drawn in the page leaves the repeat to the chip wherever the chip is already showing
  // it. One drawn nowhere — stored, or hidden — has the player as the only place its repeat can be read.
  const getChartRepeat = (chart) =>
    (!chart.isAttached && drawnChart ? hasChartRepeat : hasRepeat) ? part.repeatCount : undefined;

  // A tap on a chart opens the player on it. A chord inside a bar is still a chord first — it opens
  // the piano the way one written over a syllable does — so only the rest of the chart, the bars and
  // the space between them, is the way in here.
  const handleChartClick = (e, chart) => {
    // Same guard the lyrics take: a double tap belongs to ViewBody's auto-scroll toggle
    if (e.detail >= 2) return;
    if (openChordAt(e)) return;

    // Held back from the lyrics row underneath, which would otherwise tick the repeat counter
    // behind the modal on the way past
    e.stopPropagation();
    setSelectedChart(chart);
  };

  // This part's chart as a run through the song plays it (see ChartRunContext): what the player is
  // handed when the chart is tapped here, from the same functions, so the two can't disagree
  const runChart = canPlayChart && partChart ? { ...partChart, isAttached: !isChartDrawn(part) } : null;
  // Notes as the page styles them, for the run to show over the part they ride with
  const noteHtml = (note) =>
    `<span class="music-tag-lyrics music-tag__${partNameToClass(note.name)}">${formatPart(note.lyrics)}</span>`;
  const runShown = {
    partId: part.id,
    title: partLabel,
    name: part.name,
    number: displayNumber,
    notesHtml: (isNotePart(part) ? [part] : visibleAttachedNotes).map(noteHtml).join('\n'),
    // For the callout, which says whichever of the two the part is for — see getPartCallout. Only
    // what the chip shows, so only to the team.
    instruments: assignedInstruments,
    people: assignedPeople,
  };
  // No chart: shown in a fully charted song's run and passed over when it plays. A part that isn't a
  // note is what stops the song counting as fully charted — see ChartRunContext.
  const runShownOnly = () => ({
    ...runShown,
    lyricsHtml: isNotePart(part) || rendersNothing(repeatedLyrics) ? '' : formatPart(repeatedLyrics),
    needsChart: !isNotePart(part),
  });
  runEntryRef.current = runChart
    ? () => ({
        ...runShown,
        lines: runChart.lines,
        sections: runChart.sections,
        repeatCount: getRepeatCount(part),
        countInEntry: Boolean(runChart.countInEntry),
        shownRepeatCount: getChartRepeat(runChart),
        bpm: effectiveBpm,
        instrumentId: getChartInstrumentId(part, songChart),
        rhythmChoice: getChartRhythm(part, songChart),
        // How hard it plays through: its own, never the original's — each occurrence is arranged apart
        ...toIntensityEntry(part),
        // A part soloed on the drums plays them alone
        drumsOnly: isDrumSolo(part),
        melody: getChartMelody(runChart),
        lyricsHtml: canFollowInLyrics(runChart) ? formatPart(chartWords) : '',
        spellChord: spellPartChord,
      })
    : runShownOnly;

  return (
    // Smart auto scroll measures the rendered parts by querying these attributes, which is how it
    // picks up ViewBody's overview/attached-note filters and continuous view's part list for free
    <span key={part.id} className="ViewSongPart" data-part-id={part.id} data-song-id={songId}>
      {/* The same marker used between songs, at the compact size it takes below one — a key change
          reads the same wherever it happens, and inside a chart it is a footnote rather than a
          banner. Inside the measured element rather than above it: smart auto scroll sizes each
          part by this span's own box, so a row rendered as its sibling would add page height the
          schedule never accounts for and drift the pace from here on */}
      {showKeyChange && <SongTransition fromKey={previousPartKey} toKey={currentPartKey} />}
      {tempoEnter && (
        <PartTempoMarker
          toBpm={partBpm}
          timeSignature={songTimeSignature}
          onTap={disablePartOptions ? undefined : onTempoTap}
        />
      )}
      {renderPartChip(part)}
      {hasRowBelowChip && <br />}
      {hasCounter && (
        <>
          <RepeatCounter
            count={count}
            activeIdx={counterState.activeIdx}
            progress={counterState.progress}
            phase={counterState.phase}
            onTap={handleTap}
            partName={part.name}
          />
          <br />
        </>
      )}
      {visibleAttachedNotes.map(renderAttachedNote)}
      {hasLyricsRow && (
        <span
          className={`music-tag-lyrics music-tag__${partNameToClass(part.name)}`}
          onClick={showChordOnTap || hasCounter ? handleLyricsClick : undefined}
        >
          {renderLyrics(repeatedLyrics)}
          {drawnChart && renderChart(drawnChart, 'chart')}
        </span>
      )}
      {!endsWithChart && <br />}
      {/* In the blank line's place, and inside the measured span like the marker above */}
      {tempoExit ? (
        <PartTempoMarker
          toBpm={tempoExit}
          timeSignature={songTimeSignature}
          atFoot
          onTap={disablePartOptions ? undefined : onTempoTap}
        />
      ) : (
        <br />
      )}
      {selectedChord && (
        <ChordPiano
          chord={selectedChord}
          anchorRect={anchorRect}
          onClose={() => setSelectedChord(null)}
          family={selectedFamily}
          familyLabel={selectedFamily && getFamilyLabel(currentPartKey, selectedFamily)}
        />
      )}
      <PartOptionsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        part={selectedPart}
        onCopy={handleCopyPart}
        onEdit={handleEditPart}
        onEditIntensity={selectedPart && canArrange(selectedPart) ? handleEditIntensity : undefined}
        onPlayMidi={handlePlayMidi}
        onPlayChart={handlePlayChart}
        hasChart={canPlayAttachedChart}
        isAdmin={isAdmin}
      />
      <PartMidiModal
        isOpen={isMidiModalOpen}
        onClose={() => setIsMidiModalOpen(false)}
        part={selectedPart}
        displayNumber={displayNumber}
        songKey={currentPartKey}
        songOriginalKey={getPartOriginalKey(currentPartKey, transposeSemitones)}
        transposeSemitones={transposeSemitones}
      />
      {/* Mounted only once a chart has been tapped, so the parts of a song that nobody opens cost
          nothing — and so the player opens on its defaults each time rather than on whatever the
          last chart was left set to */}
      {selectedChart && (
        <ChordBarsPlayerModal
          isOpen
          onClose={() => setSelectedChart(null)}
          title={partLabel}
          lines={selectedChart.lines}
          // So a chart divided between instruments is the same chart in the player it was in the
          // page — the colours are what say which lines are whose, and losing them on the way in
          // would hand back a chart nobody can read
          sections={selectedChart.sections}
          repeatCount={getChartRepeat(selectedChart)}
          // Played whether or not the chart draws it, and counted the way smart scroll counts it —
          // repeat slashes typed into the words are a repeat too
          passes={getRepeatCount(part)}
          partId={part.id}
          countInEntry={Boolean(selectedChart.countInEntry)}
          bpm={effectiveBpm}
          timeSignature={songTimeSignature}
          category={songCategory}
          instrumentId={getChartInstrumentId(part, songChart)}
          songId={songId}
          rhythm={getChartRhythm(part, songChart)}
          // Played as saved; they are changed in the intensity panel
          chartEffects={songChartEffects}
          melody={getChartMelody(selectedChart)}
          // The words to follow the chart through, marked up as the page marks them up — so the
          // player's Lyrics view is this part as it is read here. Formatted at the tap rather than
          // with the part, since nothing needs it until a chart is open.
          lyricsHtml={canFollowInLyrics(selectedChart) ? formatPart(chartWords) : ''}
          spellChord={spellPartChord}
        />
      )}
    </span>
  );
};

export default ViewSongPart;
